import React, {useState} from 'react';
import {Icon} from '@/ui-kits';
import {LandingPageContent, Images, Icons} from '@/constants';
import {Routes} from '@/navigation/Routes';
import {Logo} from '@/components/common/logo';
import {TermsConditionModal} from '@/components/common/TermsConditionModal';
import {SecurityPolicyModal} from '@/components/common/SecurityPolicyModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authStatus } from '@/services/redux/selectors';

interface FooterInterface {
  variant?: 'home' | 'auth';
}
export const Footer: React.FC<FooterInterface> = props => {
  const {variant = 'home'} = props;
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(authStatus);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, link: string) => {
    e.preventDefault();
    const [path, hash] = link.split('#');
    
    if (location.pathname === path) {
      // Same page - just scroll
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Different page - navigate then scroll
      navigate(link);
    }
    
  };

  return (
    <footer className="relative flex flex-col items-center">
      {variant === 'home' && (
        <div className="mt-12">
          <Logo imageStyle="w-[90px]" textStyle='ml-1 text-xl' />
        </div>
      )}

      <div className="flex flex-col w-full md:flex-row gap-8 md:gap-20 self-center pl-6 pr-2 sm:px-4 md:px-8 md:justify-center mt-10 md:mt-48 pb-8 overflow-hidden">
        <div className="max-w-[260px] gap-6 flex flex-col">
          <p className="font-FigtreeBold md:font-FigtreeSemiBold text-lg sm:text-xl text-ui_blue break-words">
            {LandingPageContent.footer.social_media.title}
          </p>
          <p className="font-FigtreeSemiBold text-wrap md:whitespace-normal text-xs sm:text-sm text-ui_blue opacity-70 break-words">
            {LandingPageContent.footer.social_media.description}
          </p>
          <div className="hidden md:flex gap-8">
            {LandingPageContent.footer.social_media.links.map(item => (
              <a
                key={item.icon}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-70 transition-opacity cursor-pointer">
                <Icon className='size-[28px]' type={item.icon} />
              </a>
            ))}
          </div>
        </div>

        {LandingPageContent.footer.quick_links.map(item => (
          <div className="max-x-[130px] gap-6 min-w-0" key={item.title}>
            <p className="font-FigtreeBold md:font-FigtreeSemiBold text-lg sm:text-xl text-ui_blue break-words">
              {item.title}
            </p>
            <div className="gap-2 mt-4 hidden md:flex flex-col">
              {item.links.map(item => (
                <a
                  onClick={(e) => handleNavClick(e, item.link)}
                  className="text-ui_blue text-sm font-FigtreeRegular break-words"
                  key={item.label}
                  href={item.link}>
                  {item.label}
                </a>
              ))}
            </div>

            {/* new container to display links split way for mobile screen */}
            <div className="flex justify-between gap-2 sm:gap-4 mb-16 min-w-0">
              <div className="gap-2 mt-4 flex md:hidden flex-col min-w-0 flex-1">
                {item.links.slice(0, 4).map(item => (
                  <a
                    onClick={(e) => handleNavClick(e, item.link)}
                    className="text-ui_blue text-xs sm:text-sm font-FigtreeRegular break-words"
                    key={item.label}
                    href={item.link}>
                    {item.label}
                  </a>
                ))}
              </div>
              <div className="gap-2 mt-4 flex md:hidden flex-col min-w-0 flex-1">
                {item.links.slice(4).map(item => (
                  <a
                    onClick={(e) => handleNavClick(e, item.link)}
                    className="text-ui_blue text-xs sm:text-sm font-FigtreeRegular break-words"
                    key={item.label}
                    href={item.link}>
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        ))}

        <div className="flex md:hidden gap-4 sm:gap-8 justify-evenly">
          {LandingPageContent.footer.social_media.links.map(item => (
            <a
              key={item.icon}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-70 transition-opacity cursor-pointer flex-shrink-0">
              <Icon className="size-[24px] sm:size-[28px]" type={item.icon} />
            </a>
          ))}
        </div>

        <img
          alt="footer-image-2"
          src={Images.footer_image_2}
          className="absolute hidden md:block size-[250px] lg:size-[320px] 2xl:size-[450px] right-0 -z-10 bottom-0"
        />
        <img
          alt="footer-image-1"
          src={Images.footer_image_1}
          className="absolute hidden md:block size-[350px] lg:size-[420px] 2xl:size-[550px] left-[-100px] -z-10 bottom-0"
        />
      </div>
      <div className="bg-ui_blue_background flex flex-wrap gap-6 sm:gap-4 md:gap-8 lg:gap-10 self-stretch justify-center sm:justify-evenly md:justify-end p-2 sm:p-4 md:p-9">
        {LandingPageContent.footer.bottom.map(item => (
          <div key={item.label} className="flex flex-row items-center gap-2">
            <span
              onClick={e => {
                e.preventDefault();
                if (isAuthenticated) {
                  if (item.link === Routes.TERMS) {
                    setIsTermsModalOpen(true);
                  } else if (item.link === Routes.PRIVACY) {
                    setIsSecurityModalOpen(true);
                  }
                } else {
                  navigate(item.link);
                }
              }}
              className="font-FigtreeRegular text-xs sm:text-sm md:text-base text-white cursor-pointer hover:underline break-words">
              {item.label}
            </span>
          </div>
        ))}
      </div>
      <TermsConditionModal isOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} />
      <SecurityPolicyModal isOpen={isSecurityModalOpen} onClose={() => setIsSecurityModalOpen(false)} />
    </footer>
  );
};
